import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../app/hooks';
import Card from './Card';
import Invoice from './Invoice';
import { useTenant } from './TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import { vehicleLabel } from '../util/vehicleText';

interface OrderSummaryProps {
  onlyServices?: boolean;
  /** With onlyServices: also list the chosen add-ons (no fees/totals). */
  includeAddOns?: boolean;
  onDiscountUpdate?: (discountAmount: number | null,
    discountType: string | null,
    discountFeesAmount: number,
    discointCodeId?: string) => void;
  isVendor?: boolean;
  email?: string;
}

export default function OrderSummary({
  onlyServices = false,
  includeAddOns = false,
  onDiscountUpdate,
  isVendor = false,
  email,
}: OrderSummaryProps) {
  const { t } = useTranslation();
  const make = useAppSelector((state) => (
    state.car.make
  ));
  const model = useAppSelector((state) => (
    state.car.model
  ));
  const year = useAppSelector((state) => (
    state.car.year
  ));

  // Aperio: "Order summary for your 2020 Toyota Corolla" - no double space,
  // no stray line break, no capitals-only names (QA #49). Every other
  // tenant's title is unchanged.
  const themed = normalizeThemePreset(useTenant()?.themePreset) !== 'default';
  const vehicle = vehicleLabel(year, make, model);
  const orderTitle = vehicle
    ? t('summary.orderSummaryFor', { vehicle })
    : t('summary.orderSummary');
  const presetTitle = onlyServices
    ? t('summary.selectedServices')
    : orderTitle;

  const defaultTitle = onlyServices
    ? 'Selected Services'
    : 'Order Summary'
      + `${(!!make || !!model || !!year)
        ? ` for your \n ${make} ${model} ${year ? `(${year})` : ''}`
        : ' for your Vehicle'}`;

  // On a phone the card starts collapsed, which under the preset hid the
  // order summary - and the promo-code box in it - below the form until
  // its chevron was found. The preset opens the full summary (the Schedule
  // step's); every other tenant keeps today's collapsed card.
  return (
    <Card
      title={themed ? presetTitle : defaultTitle}
      openOnRender={themed && !onlyServices}
    >
      <Invoice
        onlyServices={onlyServices}
        includeAddOns={includeAddOns}
        onDiscountUpdate={onDiscountUpdate}
        isVendor={isVendor}
        email={email}
      />
    </Card>
  );
}
